import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Separator } from "../ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "sonner";
import { 
  Plus, Gavel, Eye, EyeOff, Play, Square, Trash2, 
  Bell, CheckCircle2, XCircle, Clock, DollarSign,
  Users, Calendar, Edit, MoreHorizontal, RefreshCw,
  Settings, Save, Mail, Timer, Percent
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { API } from "../../apiConfig";


// Default auction settings
const DEFAULT_AUCTION_SETTINGS = {
  default_starting_bid: 500,
  default_min_increment: 50,
  default_duration_days: 7,
  default_anti_sniping_enabled: true,
  default_anti_sniping_minutes: 5,
  default_visibility: "silent",
  auto_notify_winner: true,
  winner_notification_delay_minutes: 5,
  enable_bid_notifications: true,
  categories_enabled: ["original", "kangaroo_skin"],
  buyer_premium_percent: 0,
  reserve_price_required: false
};

const AuctionsTab = ({ token }) => {
  const [activeSubTab, setActiveSubTab] = useState("manage");
  const [auctions, setAuctions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingAuction, setEditingAuction] = useState(null);
  const [selectedAuction, setSelectedAuction] = useState(null);
  
  // Auction Settings State
  const [auctionSettings, setAuctionSettings] = useState(DEFAULT_AUCTION_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    product_id: "",
    title: "",
    description: "",
    category: "original",
    starting_bid: 500,
    min_bid_increment: 50,
    reserve_price: "",
    start_time: "",
    end_time: "",
    anti_sniping_enabled: true,
    anti_sniping_minutes: 5,
    visibility: "silent",
    admin_notes: ""
  });

  useEffect(() => {
    fetchAuctions();
    fetchProducts();
    fetchAuctionSettings();
  }, []);

  const fetchAuctionSettings = async () => {
    try {
      const response = await axios.get(`${API}/auctions/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setAuctionSettings({ ...DEFAULT_AUCTION_SETTINGS, ...response.data });
      }
    } catch (error) {
      console.log("Using default auction settings");
    }
  };

  const saveAuctionSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API}/auctions/admin/settings`, auctionSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Auction settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchAuctions = async () => {
    try {
      const response = await axios.get(`${API}/auctions/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuctions(response.data);
    } catch (error) {
      console.error("Error fetching auctions:", error);
      toast.error("Failed to load auctions");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=100`);
      setProducts(response.data.filter(p => 
        ["original", "artifact"].includes(p.category) || p.title.toLowerCase().includes("kangaroo")
      ));
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleCreateAuction = async () => {
    if (!formData.product_id || !formData.title || !formData.start_time || !formData.end_time) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const payload = {
        ...formData,
        starting_bid: parseFloat(formData.starting_bid),
        min_bid_increment: parseFloat(formData.min_bid_increment),
        reserve_price: formData.reserve_price ? parseFloat(formData.reserve_price) : null,
        anti_sniping_minutes: parseInt(formData.anti_sniping_minutes)
      };

      await axios.post(`${API}/auctions/admin/create`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Auction created successfully");
      setCreateDialogOpen(false);
      resetForm();
      fetchAuctions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create auction");
    }
  };

  const handleActivate = async (auctionId) => {
    try {
      await axios.post(`${API}/auctions/admin/${auctionId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Auction activated");
      fetchAuctions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate auction");
    }
  };

  const handleEnd = async (auctionId) => {
    if (!confirm("Are you sure you want to end this auction early?")) return;
    
    try {
      await axios.post(`${API}/auctions/admin/${auctionId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Auction ended");
      fetchAuctions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to end auction");
    }
  };

  const handleCancel = async (auctionId) => {
    if (!confirm("Are you sure you want to cancel this auction?")) return;
    
    try {
      await axios.post(`${API}/auctions/admin/${auctionId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Auction cancelled");
      fetchAuctions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel auction");
    }
  };

  const handleToggleVisibility = async (auctionId) => {
    try {
      const response = await axios.post(`${API}/auctions/admin/${auctionId}/toggle-visibility`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Visibility changed to ${response.data.visibility}`);
      fetchAuctions();
    } catch (error) {
      toast.error("Failed to toggle visibility");
    }
  };

  const handleNotifyWinner = async (auctionId) => {
    try {
      await axios.post(`${API}/auctions/admin/${auctionId}/notify-winner`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Winner notification sent");
      fetchAuctions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to notify winner");
    }
  };

  const handleMarkSold = async (auctionId) => {
    try {
      await axios.post(`${API}/auctions/admin/${auctionId}/mark-sold`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Auction marked as sold");
      fetchAuctions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as sold");
    }
  };

  const handleDelete = async (auctionId) => {
    if (!confirm("Are you sure you want to delete this draft auction?")) return;
    
    try {
      await axios.delete(`${API}/auctions/admin/${auctionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Auction deleted");
      fetchAuctions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete auction");
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: "",
      title: "",
      description: "",
      category: "original",
      starting_bid: auctionSettings.default_starting_bid,
      min_bid_increment: auctionSettings.default_min_increment,
      reserve_price: "",
      start_time: "",
      end_time: "",
      anti_sniping_enabled: auctionSettings.default_anti_sniping_enabled,
      anti_sniping_minutes: auctionSettings.default_anti_sniping_minutes,
      visibility: auctionSettings.default_visibility,
      admin_notes: ""
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: "Draft", className: "bg-gray-500" },
      scheduled: { label: "Scheduled", className: "bg-blue-500" },
      active: { label: "Live", className: "bg-green-500" },
      ended: { label: "Ended", className: "bg-yellow-500" },
      sold: { label: "Sold", className: "bg-primary" },
      no_sale: { label: "No Sale", className: "bg-red-500" },
      cancelled: { label: "Cancelled", className: "bg-gray-400" },
    };
    const badge = badges[status] || { label: status, className: "bg-gray-500" };
    return <Badge className={`${badge.className} text-white`}>{badge.label}</Badge>;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
    }).format(price || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString('en-AU', {
      dateStyle: 'short',
      timeStyle: 'short'
    });
  };

  // Set default times when opening create dialog
  const handleOpenCreate = () => {
    const now = new Date();
    const startTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now
    const durationDays = auctionSettings.default_duration_days || 7;
    const endTime = new Date(now.getTime() + durationDays * 24 * 60 * 60 * 1000);
    
    setFormData({
      ...formData,
      starting_bid: auctionSettings.default_starting_bid,
      min_bid_increment: auctionSettings.default_min_increment,
      anti_sniping_enabled: auctionSettings.default_anti_sniping_enabled,
      anti_sniping_minutes: auctionSettings.default_anti_sniping_minutes,
      visibility: auctionSettings.default_visibility,
      start_time: startTime.toISOString().slice(0, 16),
      end_time: endTime.toISOString().slice(0, 16)
    });
    setCreateDialogOpen(true);
  };

  // Settings Panel Component
  const SettingsPanel = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Default Auction Settings
          </CardTitle>
          <CardDescription>
            Configure default values for new auctions. These can be overridden when creating individual auctions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pricing Defaults */}
          <div>
            <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Pricing Defaults
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Default Starting Bid ($)</Label>
                <Input
                  type="number"
                  min="0"
                  value={auctionSettings.default_starting_bid}
                  onChange={(e) => setAuctionSettings({
                    ...auctionSettings,
                    default_starting_bid: parseFloat(e.target.value) || 0
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Default Minimum Increment ($)</Label>
                <Input
                  type="number"
                  min="1"
                  value={auctionSettings.default_min_increment}
                  onChange={(e) => setAuctionSettings({
                    ...auctionSettings,
                    default_min_increment: parseFloat(e.target.value) || 50
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Buyer Premium (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="25"
                  value={auctionSettings.buyer_premium_percent}
                  onChange={(e) => setAuctionSettings({
                    ...auctionSettings,
                    buyer_premium_percent: parseFloat(e.target.value) || 0
                  })}
                />
                <p className="text-xs text-muted-foreground">Additional fee charged to buyers (0 = none)</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Timing Defaults */}
          <div>
            <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
              <Timer className="h-4 w-4" />
              Timing Defaults
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Duration (Days)</Label>
                <Select 
                  value={auctionSettings.default_duration_days?.toString()} 
                  onValueChange={(value) => setAuctionSettings({
                    ...auctionSettings,
                    default_duration_days: parseInt(value)
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Day</SelectItem>
                    <SelectItem value="3">3 Days</SelectItem>
                    <SelectItem value="5">5 Days</SelectItem>
                    <SelectItem value="7">7 Days</SelectItem>
                    <SelectItem value="10">10 Days</SelectItem>
                    <SelectItem value="14">14 Days</SelectItem>
                    <SelectItem value="30">30 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Anti-Sniping Extension (Minutes)</Label>
                <Select 
                  value={auctionSettings.default_anti_sniping_minutes?.toString()} 
                  onValueChange={(value) => setAuctionSettings({
                    ...auctionSettings,
                    default_anti_sniping_minutes: parseInt(value)
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 Minutes</SelectItem>
                    <SelectItem value="5">5 Minutes</SelectItem>
                    <SelectItem value="10">10 Minutes</SelectItem>
                    <SelectItem value="15">15 Minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 p-3 bg-muted/50 rounded-lg">
              <div>
                <Label>Enable Anti-Sniping by Default</Label>
                <p className="text-xs text-muted-foreground">Extend auction when bids placed near end</p>
              </div>
              <Switch
                checked={auctionSettings.default_anti_sniping_enabled}
                onCheckedChange={(checked) => setAuctionSettings({
                  ...auctionSettings,
                  default_anti_sniping_enabled: checked
                })}
              />
            </div>
          </div>

          <Separator />

          {/* Visibility & Notifications */}
          <div>
            <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Visibility & Notifications
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Default Visibility</Label>
                <Select 
                  value={auctionSettings.default_visibility} 
                  onValueChange={(value) => setAuctionSettings({
                    ...auctionSettings,
                    default_visibility: value
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="silent">
                      <div className="flex items-center gap-2">
                        <EyeOff className="h-4 w-4" />
                        Silent (bids hidden until end)
                      </div>
                    </SelectItem>
                    <SelectItem value="public">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        Public (bid amounts visible)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label>Auto-Notify Winners</Label>
                  <p className="text-xs text-muted-foreground">Send email when auction ends</p>
                </div>
                <Switch
                  checked={auctionSettings.auto_notify_winner}
                  onCheckedChange={(checked) => setAuctionSettings({
                    ...auctionSettings,
                    auto_notify_winner: checked
                  })}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label>Bid Notifications</Label>
                  <p className="text-xs text-muted-foreground">Notify admin when new bids are placed</p>
                </div>
                <Switch
                  checked={auctionSettings.enable_bid_notifications}
                  onCheckedChange={(checked) => setAuctionSettings({
                    ...auctionSettings,
                    enable_bid_notifications: checked
                  })}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label>Require Reserve Price</Label>
                  <p className="text-xs text-muted-foreground">Force all auctions to have a reserve</p>
                </div>
                <Switch
                  checked={auctionSettings.reserve_price_required}
                  onCheckedChange={(checked) => setAuctionSettings({
                    ...auctionSettings,
                    reserve_price_required: checked
                  })}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Categories */}
          <div>
            <h3 className="text-sm font-medium mb-4">Enabled Categories</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { id: "original", label: "Originals" },
                { id: "kangaroo_skin", label: "Kangaroo Skins" },
                { id: "special_edition", label: "Special Editions", disabled: true }
              ].map((cat) => (
                <div 
                  key={cat.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    cat.disabled ? 'opacity-50' : ''
                  } ${
                    auctionSettings.categories_enabled?.includes(cat.id) 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border'
                  }`}
                >
                  <span className="text-sm">{cat.label}</span>
                  <Switch
                    disabled={cat.disabled}
                    checked={auctionSettings.categories_enabled?.includes(cat.id)}
                    onCheckedChange={(checked) => {
                      const current = auctionSettings.categories_enabled || [];
                      setAuctionSettings({
                        ...auctionSettings,
                        categories_enabled: checked 
                          ? [...current, cat.id]
                          : current.filter(c => c !== cat.id)
                      });
                    }}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Special Editions coming soon</p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveAuctionSettings} disabled={savingSettings}>
          {savingSettings ? (
            <>Saving...</>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Gavel className="w-5 h-5" />
            Silent Auctions
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage auction listings for originals and kangaroo skins
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAuctions}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleOpenCreate}>
            <Plus className="w-4 h-4 mr-2" />
            Create Auction
          </Button>
        </div>
      </div>

      {/* Sub-tabs for Manage / Settings */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList>
          <TabsTrigger value="manage" className="gap-2">
            <Gavel className="h-4 w-4" />
            Manage Auctions
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manage" className="mt-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100 dark:bg-green-900">
                <Play className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {auctions.filter(a => a.status === "active").length}
                </p>
                <p className="text-sm text-muted-foreground">Live Auctions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {auctions.filter(a => a.status === "scheduled").length}
                </p>
                <p className="text-sm text-muted-foreground">Scheduled</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-primary/10">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {formatPrice(auctions.filter(a => a.status === "sold").reduce((sum, a) => sum + (a.current_bid || 0), 0))}
                </p>
                <p className="text-sm text-muted-foreground">Total Sold</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-yellow-100 dark:bg-yellow-900">
                <Users className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {auctions.reduce((sum, a) => sum + (a.bid_count || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Total Bids</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Auctions Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Auctions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : auctions.length === 0 ? (
            <div className="text-center py-8">
              <Gavel className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No auctions yet</p>
              <Button className="mt-4" onClick={handleOpenCreate}>
                Create Your First Auction
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Auction</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Current Bid</TableHead>
                  <TableHead>Bids</TableHead>
                  <TableHead>Reserve</TableHead>
                  <TableHead>End Time</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auctions.map((auction) => (
                  <TableRow key={auction.auction_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {auction.product_images?.[0] && (
                          <img 
                            src={auction.product_images[0]} 
                            alt="" 
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        )}
                        <div>
                          <p className="font-medium line-clamp-1">{auction.title}</p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {auction.category?.replace("_", " ")}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(auction.status)}
                        {auction.visibility === "silent" ? (
                          <EyeOff className="h-3 w-3 text-muted-foreground" />
                        ) : (
                          <Eye className="h-3 w-3 text-muted-foreground" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">
                        {auction.current_bid > 0 ? formatPrice(auction.current_bid) : formatPrice(auction.starting_bid)}
                      </span>
                      {auction.current_bid === 0 && (
                        <span className="text-xs text-muted-foreground ml-1">(starting)</span>
                      )}
                    </TableCell>
                    <TableCell>{auction.bid_count || 0}</TableCell>
                    <TableCell>
                      {auction.reserve_price ? (
                        <div className="flex items-center gap-1">
                          {formatPrice(auction.reserve_price)}
                          {auction.reserve_met ? (
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                          ) : (
                            <XCircle className="h-3 w-3 text-red-500" />
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDate(auction.end_time)}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {auction.status === "draft" && (
                            <DropdownMenuItem onClick={() => handleActivate(auction.auction_id)}>
                              <Play className="h-4 w-4 mr-2" />
                              Activate
                            </DropdownMenuItem>
                          )}
                          {auction.status === "scheduled" && (
                            <DropdownMenuItem onClick={() => handleActivate(auction.auction_id)}>
                              <Play className="h-4 w-4 mr-2" />
                              Start Now
                            </DropdownMenuItem>
                          )}
                          {auction.status === "active" && (
                            <>
                              <DropdownMenuItem onClick={() => handleToggleVisibility(auction.auction_id)}>
                                {auction.visibility === "silent" ? (
                                  <>
                                    <Eye className="h-4 w-4 mr-2" />
                                    Make Bids Public
                                  </>
                                ) : (
                                  <>
                                    <EyeOff className="h-4 w-4 mr-2" />
                                    Make Silent
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEnd(auction.auction_id)}>
                                <Square className="h-4 w-4 mr-2" />
                                End Auction
                              </DropdownMenuItem>
                            </>
                          )}
                          {auction.status === "ended" && (
                            <>
                              <DropdownMenuItem onClick={() => handleNotifyWinner(auction.auction_id)}>
                                <Bell className="h-4 w-4 mr-2" />
                                Notify Winner
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleMarkSold(auction.auction_id)}>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Mark as Sold
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          {!["sold", "active"].includes(auction.status) && (
                            <DropdownMenuItem onClick={() => handleCancel(auction.auction_id)}>
                              <XCircle className="h-4 w-4 mr-2" />
                              Cancel
                            </DropdownMenuItem>
                          )}
                          {auction.status === "draft" && (
                            <DropdownMenuItem 
                              onClick={() => handleDelete(auction.auction_id)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <SettingsPanel />
        </TabsContent>
      </Tabs>

      {/* Create Auction Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Auction</DialogTitle>
            <DialogDescription>
              Set up a new silent auction for an original artwork or kangaroo skin.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Product Selection */}
            <div className="space-y-2">
              <Label>Select Product *</Label>
              <Select 
                value={formData.product_id} 
                onValueChange={(value) => {
                  const product = products.find(p => p.product_id === value);
                  setFormData({
                    ...formData,
                    product_id: value,
                    title: product ? `${product.title} - Silent Auction` : formData.title
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(product => (
                    <SelectItem key={product.product_id} value={product.product_id}>
                      {product.title} ({product.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Auction Title */}
            <div className="space-y-2">
              <Label>Auction Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Original Artwork - Spirit of the Land"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the auction item..."
                rows={3}
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="original">Original Artwork</SelectItem>
                  <SelectItem value="kangaroo_skin">Kangaroo Skin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Pricing */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Starting Bid ($) *</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.starting_bid}
                  onChange={(e) => setFormData({ ...formData, starting_bid: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Min Increment ($)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.min_bid_increment}
                  onChange={(e) => setFormData({ ...formData, min_bid_increment: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Reserve Price ($)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.reserve_price}
                  onChange={(e) => setFormData({ ...formData, reserve_price: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>

            <Separator />

            {/* Timing */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                />
              </div>
            </div>

            <Separator />

            {/* Options */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Anti-Sniping Protection</Label>
                  <p className="text-xs text-muted-foreground">
                    Extend auction if bid placed in last {formData.anti_sniping_minutes} minutes
                  </p>
                </div>
                <Switch
                  checked={formData.anti_sniping_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, anti_sniping_enabled: checked })}
                />
              </div>

              <div className="space-y-2">
                <Label>Visibility</Label>
                <Select 
                  value={formData.visibility} 
                  onValueChange={(value) => setFormData({ ...formData, visibility: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="silent">
                      <div className="flex items-center gap-2">
                        <EyeOff className="h-4 w-4" />
                        Silent (bids hidden)
                      </div>
                    </SelectItem>
                    <SelectItem value="public">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        Public (bid amounts visible)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Admin Notes */}
            <div className="space-y-2">
              <Label>Admin Notes (private)</Label>
              <Textarea
                value={formData.admin_notes}
                onChange={(e) => setFormData({ ...formData, admin_notes: e.target.value })}
                placeholder="Internal notes about this auction..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAuction}>
              Create Auction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuctionsTab;
